import React from 'react';
import {
  DailyStockEntry,
  Rhythmus,
  calculateStockSimulation,
  calculateSimulationDays,
  formatDate,
  formatDateFull,
  getNextOrderDay,
  calculateDeliveryDate,
  isOrderDay,
  addDays,
  getNextDeliveryDay,
} from './dispoUtils';
import './DispoContent.css';

interface DispoSimulationTableProps {
  startDate: Date;
  bestand: number;
  dispoBestand: number; // Bestand + offene Lieferungen
  wochenAV: number;
  bestellmenge: number;
  rhythmus: Rhythmus;
}

const DispoSimulationTable: React.FC<DispoSimulationTableProps> = ({
  startDate,
  bestand,
  dispoBestand,
  wochenAV,
  bestellmenge,
  rhythmus,
}) => {
  // Offene Lieferung = Dispo-Bestand - aktueller Bestand
  const offeneLieferung = Math.max(0, dispoBestand - bestand);

  // Berechne dynamisch die Simulationstage basierend auf dem Rhythmus
  const simulationDays = calculateSimulationDays(startDate, rhythmus);
  const entries = calculateStockSimulation(
    startDate,
    simulationDays,
    bestand,
    wochenAV,
    bestellmenge,
    rhythmus,
    true, // Simuliere Bestellung am Starttag wenn möglich
    offeneLieferung // Offene Lieferung am ersten Liefertag
  );

  // Prüfe ob Unterdeckung VOR dem letzten Liefertag auftritt
  // Am letzten Liefertag könnte ja wieder Ware kommen
  const lastDeliveryIndex = entries.reduce((lastIdx, entry, idx) =>
    entry.isDeliveryDay ? idx : lastIdx, -1);

  const hasNegativeBeforeLastDelivery = entries.some((entry, idx) =>
    entry.isNegative && idx < lastDeliveryIndex
  );

  // Berechne Info für die Anzeige
  const nextOrderDay = getNextOrderDay(startDate, rhythmus);
  const deliveryDate = calculateDeliveryDate(nextOrderDay, rhythmus);
  const deliveryDateKey = deliveryDate ? deliveryDate.toISOString().split('T')[0] : null;
  const nextDeliveryAfter = deliveryDate
    ? getNextDeliveryDay(addDays(deliveryDate, 1), rhythmus)
    : null;
  const nextDeliveryAfterKey = nextDeliveryAfter ? nextDeliveryAfter.toISOString().split('T')[0] : null;
  const isTodayOrderDay = isOrderDay(startDate, rhythmus);

  return (
    <div className="dispo-simulation">
      <div className="simulation-header">
        <h3>Bestandssimulation</h3>
        <div className="simulation-info">
          {isTodayOrderDay ? (
            <span className="info-badge order">
              Heute ist Bestelltag → Lieferung {deliveryDate ? formatDateFull(deliveryDate) : ''}
            </span>
          ) : (
            <span className="info-badge">
              Nächster Bestelltag: {formatDateFull(nextOrderDay)}
            </span>
          )}

        </div>
      </div>

      <div className="table-container">
        <table className="simulation-table">
          <thead>
            <tr>
              <th className="col-day">Tag</th>
              <th className="col-date">Datum</th>
              <th className="col-av">AV</th>
              <th className="col-delivery">WE</th>
              <th className="col-stock">Bestand</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((entry, index) => {
              const entryDateKey = entry.date.toISOString().split('T')[0];
              const isOrderDeliveryDay = entryDateKey === deliveryDateKey;
              const isNextPossibleDelivery = entryDateKey === nextDeliveryAfterKey;
              return (
                <TableRow
                  key={index}
                  entry={entry}
                  isLastDeliveryDay={index >= lastDeliveryIndex}
                  isOrderDeliveryDay={isOrderDeliveryDay}
                  isNextPossibleDelivery={isNextPossibleDelivery}
                />
              );
            })}
          </tbody>
        </table>
      </div>




    </div>
  );
};

interface TableRowProps {
  entry: DailyStockEntry;
  isLastDeliveryDay: boolean;
  isOrderDeliveryDay: boolean; // Liefertag der aktuellen Bestellung
  isNextPossibleDelivery: boolean; // Nächster möglicher Liefertag (nächste Dispo)
}

const TableRow: React.FC<TableRowProps> = ({ entry, isLastDeliveryDay, isOrderDeliveryDay, isNextPossibleDelivery }) => {
  // Vor dem ersten beeinflussbaren Liefertag: keine Warnung, Bestand min. 0 anzeigen
  const displayStock = entry.isNegative && !isOrderDeliveryDay && !isNextPossibleDelivery && !isLastDeliveryDay
    ? 0
    : Math.round(entry.stockEnd);
  
  // Nur Warnung zeigen wenn negativ UND nach dem beeinflussbaren Zeitraum
  const showNegativeWarning = entry.isNegative && (isOrderDeliveryDay || isNextPossibleDelivery || isLastDeliveryDay);

  const rowClasses = [
    entry.isSunday ? 'sunday-row' : '',
    showNegativeWarning ? 'negative-row' : '',
    isOrderDeliveryDay ? 'order-delivery-row' : '',
    isNextPossibleDelivery ? 'next-delivery-row' : '',
  ].filter(Boolean).join(' ');

  // Wareneingang = offene Lieferung + simulierte Bestellung
  const wareneingang = entry.simulatedOrder;
  
  // Kurzer Tagesname für Mobile (Mo, Di, Mi...)
  const shortDayName = entry.dayName.substring(0, 2);

  return (
    <tr className={rowClasses}>
      <td className="day-cell">
        <span className="day-short">{shortDayName}</span>
        <span className="day-full">{entry.dayName}</span>
      </td>
      <td>{formatDate(entry.date)}</td>
      <td>
        {entry.isSunday ? '' : `-${Math.round(entry.dailyAV)}`}
      </td>
      <td className={wareneingang > 0 ? 'simulated-value' : ''}>
        {entry.isSunday ? '' : (wareneingang > 0 ? `+${Math.round(wareneingang)}` : '-')}
      </td>
      <td className={`stock-end ${showNegativeWarning ? 'negative-value' : ''}`}>
        {entry.isSunday ? '' : displayStock}
        {showNegativeWarning && !entry.isSunday && <span className="warning-icon">⚠️</span>}
      </td>
    </tr>
  );
};

export default DispoSimulationTable;
